import {
  createContext,
  useReducer,
  useContext,
  useState,
  useEffect,
} from 'react';

import type { Dispatch, ReactNode } from 'react';
import type {
  IUnreadInAppMessageData,
  ISuggestedProductModal,
  BoostModalData,
  SellerAnalyticsModalDisplayType,
  ConsentOptions,
  UserCookieInfo,
} from '../types';
import type { UserReducerAction } from '../contexts/reducers/userReducer';

import { userReducer } from '../contexts/reducers/userReducer';
import { useRouter } from 'next/router';
import type { NotificationFrequency } from '../components/SearchPage/hooks/useSavedSearch/useSavedSearch.typed';
import { useCookieExpiry } from '../hooks/UseCookieExpiry';
import { ENABLE_USER_PROFILE_ENDPOINT, ENVIRONMENT_NAME } from '../utils';
import { signIn, useSession } from 'next-auth/react';
import { userLogout } from '../helpers/Logout';
import { fireToast } from '../helpers/Toasts';
import { keycloakLogout } from '../utils/keycloakLogout';
import { UserProfile } from '../api/schemas/user-profile.schema';

interface UserProviderProps {
  children: ReactNode;
  initialUser: UserProfile | undefined;
  userCookie: UserCookieInfo | undefined;
  consentOptions: ConsentOptions;
}

interface IHistoryCheckModalData {
  title?: string;
}

interface UserContextI {
  userProfile: UserProfile | undefined;
  dispatchUserProfile: Dispatch<UserReducerAction>;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (t: boolean) => void;
  consentOptions: ConsentOptions;
  isInAppMessageSimpleModalOpen: boolean;
  setIsInAppMessageSimpleModalOpen: (t: boolean) => void;
  inAppMessageData?: IUnreadInAppMessageData;
  setInAppMessageData: (t: IUnreadInAppMessageData | undefined) => void;
  isBoostModalOpen: boolean;
  setIsBoostModalOpen: (t: boolean) => void;
  boostModalData: BoostModalData | undefined;
  setBoostModalData: (t: BoostModalData | undefined) => void;
  isSuggestedProductModalOpen: boolean;
  setIsSuggestedProductModalOpen: (t: boolean) => void;
  suggestedProductModalData?: ISuggestedProductModal;
  setSuggestedProductModalData: (t: ISuggestedProductModal | undefined) => void;
  currency: 'EUR' | 'GBP' | undefined;
  setCurrency: (t: 'EUR' | 'GBP' | undefined) => void;
  selectedBundle?: number;
  setSelectedBundle: (bundleID: number | undefined) => void;
  isHistoryCheckModalOpen: boolean;
  setIsHistoryCheckModalOpen: (t: boolean) => void;
  historyCheckModalData: IHistoryCheckModalData | undefined;
  setHistoryCheckModalData: (t: IHistoryCheckModalData | undefined) => void;
  isSavedSearchModalOpen: boolean;
  setIsSavedSearchModalOpen: (t: boolean) => void;
  isInstantAlertAvailable: boolean;
  setIsInstantAlertAvailable: (t: boolean) => void;
  isSellerAnalyticsModalOpen: boolean;
  setIsSellerAnalyticsModalOpen: (t: boolean) => void;
  sellerAnalyticsModalDisplay?: SellerAnalyticsModalDisplayType;
  setSellerAnalyticsModalDisplay: (
    t: SellerAnalyticsModalDisplayType | undefined,
  ) => void;
  isSearchSaved: boolean;
  setIsSearchSaved: (saved: boolean) => void;
  selectedNotificationFrequency?: NotificationFrequency;
  setSelectedNotificationFrequency: (frequency: NotificationFrequency) => void;
  isTrustedDealerModalOpen: boolean;
  setIsTrustedDealerModalOpen: (t: boolean) => void;
  isBrazeInitialized: boolean;
  setIsBrazeInitialized: (t: boolean) => void;
  isUserVerified: boolean;
}

const UserContext = createContext<UserContextI>({
  userProfile: undefined,
  dispatchUserProfile: () => null,
  isShareModalOpen: false,
  setIsShareModalOpen: () => null,
  isInAppMessageSimpleModalOpen: false,
  setIsInAppMessageSimpleModalOpen: () => null,
  inAppMessageData: undefined,
  setInAppMessageData: () => null,
  isBoostModalOpen: false,
  setIsBoostModalOpen: () => null,
  boostModalData: undefined,
  setBoostModalData: () => null,
  isSuggestedProductModalOpen: false,
  setIsSuggestedProductModalOpen: () => null,
  suggestedProductModalData: undefined,
  setSuggestedProductModalData: () => null,
  currency: undefined,
  setCurrency: () => null,
  selectedBundle: undefined,
  setSelectedBundle: () => null,
  isHistoryCheckModalOpen: false,
  setIsHistoryCheckModalOpen: () => null,
  historyCheckModalData: undefined,
  setHistoryCheckModalData: () => null,
  isSavedSearchModalOpen: false,
  setIsSavedSearchModalOpen: () => null,
  isInstantAlertAvailable: false,
  setIsInstantAlertAvailable: () => null,
  isSellerAnalyticsModalOpen: false,
  setIsSellerAnalyticsModalOpen: () => null,
  sellerAnalyticsModalDisplay: undefined,
  setSellerAnalyticsModalDisplay: () => null,
  consentOptions: {
    functionalConsent: false,
    allowGoogle: false,
    allowWootric: false,
    allowBraze: false,
    allowConvert: false,
  },
  isSearchSaved: false,
  setIsSearchSaved: () => null,
  selectedNotificationFrequency: undefined,
  setSelectedNotificationFrequency: () => null,
  isTrustedDealerModalOpen: false,
  setIsTrustedDealerModalOpen: () => null,
  isBrazeInitialized: false,
  setIsBrazeInitialized: () => null,
  isUserVerified: false,
});

const useAuthOptions = () => {
  const { dispatchUserProfile } = useUserContext();
  const { data: session, status } = useSession();
  const router = useRouter();
  const asPath = router.asPath;
  const isLoggedInKeycloakUser = !!session?.user;
  const shouldReturnAccessToken = !!session?.user && !session?.error;

  const handleLogin = (url?: string) => {
    signIn('keycloak', {
      callbackUrl:
        typeof url === 'string'
          ? `/signin?callbackUrl=${url}`
          : `/signin?callbackUrl=${asPath}`,
    });
  };

  const handleLogout = async (
    callbackUrl?: string,
    userCookie?: UserCookieInfo,
  ) => {
    try {
      await keycloakLogout(callbackUrl);
    } catch (error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return;
    }

    /**
     * This cookie is the only method of authentication on C2B pages for users with dealer accounts.
     *
     * However, if one of these users visits a marketplace page, we log them out immediately to avoid using state derived
     * from this cookie. Cookies with dealer user data are for C2B pages only and never marketplace pages
     */
    if (
      !ENABLE_USER_PROFILE_ENDPOINT &&
      (!userCookie || userCookie?.type !== 'DEALER')
    ) {
      await userLogout(dispatchUserProfile, userCookie?.name ?? '');
    }
  };

  return {
    handleLogin,
    handleLogout,
    isAuthenticated: isLoggedInKeycloakUser,
    accessToken: shouldReturnAccessToken ? session?.accessToken : undefined,
    status,
    user: session?.user,
  };
};

const mapLegacyCookie = (userCookie: UserCookieInfo | undefined) => {
  if (userCookie) {
    return {
      name: userCookie.name,
      verification: {
        phone: userCookie.verification.phone,
        email: userCookie.verification.email,
      },
      registrationDate: userCookie.registrationdate,
    };
  }
  return undefined;
};

function UserProvider({
  children,
  initialUser,
  userCookie,
  consentOptions,
}: UserProviderProps) {
  /**
   * Legacy authentication relied on cookies to store user data and authenticate requests.
   *
   * This cookie is still required for those pages that do not use Keycloak for user information
   * that is not being derived from the session.
   *
   * Once all of data is derived from the session rather than the cookie, references to this cookie
   * may be removed from this hook.
   */
  const [userProfile, dispatchUserProfile] = useReducer(
    userReducer,
    ENABLE_USER_PROFILE_ENDPOINT ? initialUser : mapLegacyCookie(userCookie),
  );
  const { status, handleLogout } = useAuthOptions();
  const router = useRouter();

  useEffect(() => {
    if (
      !ENABLE_USER_PROFILE_ENDPOINT &&
      userCookie?.type === 'DEALER' &&
      status === 'authenticated'
    ) {
      // if the page is authenticated it will redirect to home page for us
      handleLogout(router.asPath, userCookie);
    }
    // TODO Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCookie, status]);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isInAppMessageSimpleModalOpen, setIsInAppMessageSimpleModalOpen] =
    useState(false);
  const [inAppMessageData, setInAppMessageData] =
    useState<IUnreadInAppMessageData>();
  const [isBoostModalOpen, setIsBoostModalOpen] = useState<boolean>(false);
  const [boostModalData, setBoostModalData] = useState<
    BoostModalData | undefined
  >(undefined);
  const [isSuggestedProductModalOpen, setIsSuggestedProductModalOpen] =
    useState<boolean>(false);
  const [suggestedProductModalData, setSuggestedProductModalData] = useState<
    ISuggestedProductModal | undefined
  >(undefined);
  const [currency, setCurrency] = useState<'EUR' | 'GBP' | undefined>(
    undefined,
  );
  const [selectedBundle, setSelectedBundle] = useState<number | undefined>(
    undefined,
  );
  const [isHistoryCheckModalOpen, setIsHistoryCheckModalOpen] =
    useState<boolean>(false);
  const [historyCheckModalData, setHistoryCheckModalData] = useState<
    IHistoryCheckModalData | undefined
  >(undefined);
  const [isSavedSearchModalOpen, setIsSavedSearchModalOpen] =
    useState<boolean>(false);
  const [isInstantAlertAvailable, setIsInstantAlertAvailable] =
    useState<boolean>(false);
  const [isSellerAnalyticsModalOpen, setIsSellerAnalyticsModalOpen] =
    useState<boolean>(false);
  const [sellerAnalyticsModalDisplay, setSellerAnalyticsModalDisplay] =
    useState<SellerAnalyticsModalDisplayType | undefined>(undefined);
  const [isSearchSaved, setIsSearchSaved] = useState<boolean>(false);
  const [selectedNotificationFrequency, setSelectedNotificationFrequency] =
    useState<NotificationFrequency | undefined>();
  const [isTrustedDealerModalOpen, setIsTrustedDealerModalOpen] =
    useState<boolean>(false);
  const [isBrazeInitialized, setIsBrazeInitialized] = useState<boolean>(false);

  const isUserVerified = Boolean(
    userProfile?.verification.email || userProfile?.verification.phone,
  );

  /**
   * We check the initial state of the cookie, based on what was set server-side rather than in state client-side,
   * to avoid race conditions.
   *
   * This may happen when the authentication status has resolved to 'authenticated' after 'loading',
   * before the state associated with the cookie-based user has been set with the cookie value,
   * leading to the signout function being executed too early.
   */
  useCookieExpiry({
    authenticationStatus: status,
    userCookie,
    environmentName: ENVIRONMENT_NAME,
    userProfileIsEnabled: ENABLE_USER_PROFILE_ENDPOINT,
  });

  return (
    <UserContext.Provider
      value={{
        userProfile,
        dispatchUserProfile,
        isShareModalOpen,
        setIsShareModalOpen,
        isInAppMessageSimpleModalOpen,
        setIsInAppMessageSimpleModalOpen,
        inAppMessageData,
        setInAppMessageData,
        isBoostModalOpen,
        setIsBoostModalOpen,
        boostModalData,
        setBoostModalData,
        isSuggestedProductModalOpen,
        setIsSuggestedProductModalOpen,
        suggestedProductModalData,
        setSuggestedProductModalData,
        currency,
        setCurrency,
        selectedBundle,
        setSelectedBundle,
        isHistoryCheckModalOpen,
        setIsHistoryCheckModalOpen,
        historyCheckModalData,
        setHistoryCheckModalData,
        isSavedSearchModalOpen,
        setIsSavedSearchModalOpen,
        isInstantAlertAvailable,
        setIsInstantAlertAvailable,
        isSellerAnalyticsModalOpen,
        setIsSellerAnalyticsModalOpen,
        sellerAnalyticsModalDisplay,
        setSellerAnalyticsModalDisplay,
        consentOptions,
        isSearchSaved,
        setIsSearchSaved,
        selectedNotificationFrequency,
        setSelectedNotificationFrequency,
        isTrustedDealerModalOpen,
        setIsTrustedDealerModalOpen,
        isBrazeInitialized,
        setIsBrazeInitialized,
        isUserVerified,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

const useUserContext = () => useContext(UserContext);

export { UserProvider, useUserContext, useAuthOptions };
